import { getEnvironment, UserProfile } from '@sunrun/sales-experience-shared';
import { Prospect } from 'types/prospect';

const getPartnerBaseUrl = (env: string, configured?: string): string =>
  configured ??
  (env === 'production'
    ? 'https://sunrun.my.site.com'
    : 'https://sunrun--uat.sandbox.my.site.com');

const getSalesforceBaseUrl = (env: string, configured?: string): string =>
  configured ??
  (env === 'production'
    ? 'https://sunrun.my.salesforce.com'
    : 'https://sunrun--uat.sandbox.my.salesforce.com');

type CustomerProfileQueryOptions = {
  initialTab?: string;
  openPendingTask?: boolean;
  hideTopNavigation?: boolean;
  markClosedLost?: boolean;
};

type OpenCustomerProfileOptions = {
  openPendingTask?: boolean;
  markClosedLost?: boolean;
};

const getCustomerProfileQuery = (
  options: CustomerProfileQueryOptions = {},
): string => {
  const params = new URLSearchParams();
  if (options.initialTab) {
    params.set('initialTab', options.initialTab);
  }
  if (options.openPendingTask) {
    params.set('openPendingTask', 'true');
  }
  if (options.hideTopNavigation) {
    params.set('hideTopNavigation', 'true');
  }
  if (options.markClosedLost) {
    params.set('markClosedLost', 'true');
  }
  const query = params.toString();
  return query ? `?${query}` : '';
};

const navigateToCustomerProfile = (
  prospectId: string,
  options?: OpenCustomerProfileOptions,
) => {
  const env = getEnvironment();
  const oneBaseUrl =
    env === 'production'
      ? 'https://sunrunone.com'
      : 'https://staging.sunrunone.com';
  const query = options
    ? getCustomerProfileQuery({
        initialTab: options.markClosedLost ? 'account' : 'status',
        openPendingTask: options.openPendingTask,
        markClosedLost: options.markClosedLost,
      })
    : '';
  window.open(
    `${oneBaseUrl}/customers/customer-profile/${prospectId}${query}`,
    '_blank',
  );
};

const navigateToProspect = (prospectId: string) => {
  const env = getEnvironment();
  const oneBaseUrl =
    env === 'production'
      ? 'https://sunrunone.com'
      : 'https://staging.sunrunone.com';
  window.parent.postMessage(
    {
      type: 'COMMAND:CHANGE_URL',
      source: 'Dashboard',
      payload: {
        url: `${oneBaseUrl}/customers/prospects/${prospectId}`,
      },
    },
    '*',
  );
};

const viewInSalesforce = (salesforceUrl: string) => {
  window.open(salesforceUrl, '_blank');
};

const copyCustomerInformation = (prospect: Prospect) => {
  const {
    customerFirstName,
    customerLastName,
    customerStreet,
    customerCity,
    customerState,
    customerZipCode,
  } = prospect;
  const name = `${customerFirstName || ''} ${customerLastName || ''}`.trim();
  const address = `${customerStreet || ''} ${customerCity || ''} ${
    customerState || ''
  } ${customerZipCode || ''}`.trim();
  const prospectId = prospect.prospectId;
  navigator.clipboard.writeText(
    `Customer: ${name}\nAddress: ${address}\nProspect: ${prospectId}`,
  );
};

const shouldHideSalesToolsWarning = (
  userProfile: UserProfile | undefined,
  splatUserType: string,
): 'No' | 'Yes' => {
  const splatUserTypeIncluded = [
    'CX Pro',
    'Fusion Athlete',
    'Retail Advisor',
    'Third Party Retail Advisor',
  ].includes(splatUserType ?? '');
  const titleContainsAthlete =
    userProfile?.title?.toLowerCase().indexOf('athlete') !== -1;
  const shouldHideWarning =
    !(splatUserTypeIncluded || titleContainsAthlete) &&
    userProfile?.sellsSunrun?.toLocaleLowerCase() === 'no'
      ? 'No'
      : 'Yes';
  return shouldHideWarning;
};

const viewOfferPresentation = (
  userProfile: UserProfile,
  prospectId: string,
  splatBaseUrl: string,
  hybridToken: string,
  hybridTokenType: string,
) => {
  const { username, email, splatRole } = userProfile ?? {};
  const userName = encodeURIComponent(username ?? '');
  const userEmail = encodeURIComponent(email ?? '');
  const userRole = encodeURIComponent(splatRole ?? '');

  const redirectUrl = encodeURIComponent(
    `/go-solar/${prospectId}?prospectId=${prospectId}&username=${userName}&email=${userEmail}&role=${userRole}`,
  );
  const url = `${splatBaseUrl}/login?token=${hybridToken}&tokenType=hybrid-${hybridTokenType}&redirect=${redirectUrl}`;
  window.open(url, '_blank');
};

export {
  getCustomerProfileQuery,
  navigateToCustomerProfile,
  navigateToProspect,
  viewInSalesforce,
  copyCustomerInformation,
  viewOfferPresentation,
  shouldHideSalesToolsWarning,
  getPartnerBaseUrl,
  getSalesforceBaseUrl,
};
export type { CustomerProfileQueryOptions, OpenCustomerProfileOptions };
